import {useMemo} from 'react'
import {
  Pressable,
  type GestureResponderEvent,
  View,
  type StyleProp,
  type ViewStyle,
} from 'react-native'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {getBadgesForActor} from '#/lib/badges/getBadgesForActor'
import type {Badge} from '#/lib/badges/badgeTypes'
import {atoms as a, useTheme} from '#/alf'
import {useDialogControl} from '#/components/Dialog'
import {Text} from '#/components/Typography'
import {BadgeDetailsSheet} from './BadgeDetailsSheet'
import {getBadgeVisual} from './badgeUtils'

type ActorLike = Parameters<typeof getBadgesForActor>[0]

export function BadgeIcon({
  actor,
  badges,
  maxIcons = 2,
  size = 12,
  style,
}: {
  actor?: ActorLike
  badges?: Badge[]
  maxIcons?: number
  size?: number
  style?: StyleProp<ViewStyle>
}) {
  const t = useTheme()
  const {_} = useLingui()
  const control = useDialogControl()
  const resolvedBadges = useMemo(() => {
    if (badges) return badges
    return actor ? getBadgesForActor(actor) : []
  }, [actor, badges])

  const visibleBadges = resolvedBadges.slice(0, maxIcons)
  const overflow = resolvedBadges.length - visibleBadges.length

  if (!resolvedBadges.length) return null

  const openDetails = (event?: GestureResponderEvent) => {
    event?.preventDefault?.()
    event?.stopPropagation?.()
    control.open()
  }

  return (
    <View style={[a.flex_row, a.align_center, a.gap_2xs, style]}>
      {visibleBadges.map(badge => {
        const {Icon, color} = getBadgeVisual(badge, t)
        return (
          <Pressable
            key={`${badge.source}-${badge.type}-${badge.org?.id || ''}`}
            onPress={openDetails}
            accessibilityRole="button"
            accessibilityLabel={badge.label}
            accessibilityHint={_(msg`Opens badge details`)}>
            <Icon width={size} height={size} fill={color} />
          </Pressable>
        )
      })}
      {overflow > 0 && (
        <Pressable
          onPress={openDetails}
          accessibilityRole="button"
          accessibilityLabel={_(msg`${overflow} more badges`)}
          accessibilityHint={_(msg`Opens badge details`)}>
          <View
            style={[
              a.rounded_full,
              a.px_2xs,
              a.py_2xs,
              t.atoms.bg_contrast_25,
            ]}>
            <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
              +{overflow}
            </Text>
          </View>
        </Pressable>
      )}

      <BadgeDetailsSheet control={control} badges={resolvedBadges} />
    </View>
  )
}
